import { useMemo } from "react";

import { Typography } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { DATE_TIME_FORMAT, formatDateTime } from "@shared";

import { PriorityRequestResponse } from "@/api";
import { ClientLink } from "@/common/components/client-link/ClientLink";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { PriorityRequestRowActions } from "@/pages/priority-requests/PriorityRequests.types";
import { useTranslation } from "@/translations";

import { PriorityRequestRowMenu } from "../components/PriorityRequestRowMenu";
import { ReachabilityCell } from "../components/ReachabilityCell";

type Cell = GridRenderCellParams<PriorityRequestResponse>;

const ACTIONS_COLUMN_WIDTH = 64;

const clientName = (row: PriorityRequestResponse) => `${row.firstName} ${row.lastName}`;

export function usePriorityRequestsColumns({
  onPullForward,
  onContactAttempt,
  onDelete,
  isBusy,
}: PriorityRequestRowActions): Array<GridColDef<PriorityRequestResponse>> {
  const { t } = useTranslation();
  const { language } = useLanguageContext();

  return useMemo(() => {
    const locale = locales[language];

    const columns: Array<GridColDef<PriorityRequestResponse>> = [
      {
        field: "caseNumber",
        headerName: t("priorityRequests.columns.caseNumber"),
        flex: 1,
        minWidth: 140,
        renderCell: (params: Cell) => (
          <ClientLink clientId={params.row.clientId} name={params.row.caseNumber} variant="body2" />
        ),
      },
      {
        field: "lastName",
        headerName: t("priorityRequests.columns.client"),
        flex: 2,
        minWidth: 200,
        valueGetter: (_value, row) => clientName(row),
        renderCell: (params: Cell) => (
          <ClientLink clientId={params.row.clientId} name={clientName(params.row)} variant="body2" />
        ),
      },
      {
        field: "formFillPercent",
        headerName: t("priorityRequests.columns.form"),
        type: "number",
        align: "left",
        headerAlign: "left",
        flex: 1,
        minWidth: 110,
        renderCell: (params: Cell) => (
          <Typography variant="body2" fontWeight="bold">
            {`${params.row.formFillPercent}%`}
          </Typography>
        ),
      },
      {
        field: "appointmentStartUtc",
        headerName: t("priorityRequests.columns.appointment"),
        type: "dateTime",
        flex: 1,
        minWidth: 150,
        renderCell: (params: Cell) => formatDateTime(params.row.appointmentStartUtc, DATE_TIME_FORMAT, locale),
      },
      {
        field: "counselorUserCode",
        headerName: t("priorityRequests.columns.counselor"),
        flex: 1,
        minWidth: 100,
      },
      {
        field: "reachabilityWindows",
        headerName: t("priorityRequests.columns.reachability"),
        flex: 2,
        minWidth: 220,
        sortable: false,
        renderCell: (params: Cell) => <ReachabilityCell windows={params.row.reachabilityWindows} />,
      },
      {
        field: "lastContactAttemptUtc",
        headerName: t("priorityRequests.columns.lastContactAttempt"),
        type: "dateTime",
        flex: 1,
        minWidth: 220,
        renderCell: (params: Cell) =>
          params.row.lastContactAttemptUtc ? (
            formatDateTime(params.row.lastContactAttemptUtc, DATE_TIME_FORMAT, locale)
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("priorityRequests.noContactAttempt")}
            </Typography>
          ),
      },
      {
        field: "actions",
        headerName: "",
        width: ACTIONS_COLUMN_WIDTH,
        sortable: false,
        filterable: false,
        align: "right",
        renderCell: (params: Cell) => (
          <PriorityRequestRowMenu
            name={clientName(params.row)}
            onPullForward={() => onPullForward(params.row)}
            onContactAttempt={() => onContactAttempt(params.row)}
            onDelete={() => onDelete(params.row)}
            isBusy={isBusy}
          />
        ),
      },
    ];

    return columns.map((column) => ({ ...column, display: "flex" }));
  }, [t, language, onPullForward, onContactAttempt, onDelete, isBusy]);
}
